/** Switch among the independent settings cards for enabled Kuali modules. */
import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { cn } from '@/lib/utils'

import type {
  KualiResearchFeedOptions,
  KualiResearchModuleOptions,
  KualiResearchSourceType,
} from '../types'
import { KualiResearchModuleScope } from './KualiResearchModuleScope'
import { KUALI_RESEARCH_SOURCES } from './KualiResearchSourceSelection'

export function KualiResearchModuleTabs({
  api,
  orgId,
  vaultId,
  open,
  apiUrl,
  credentialStored,
  credentialSource,
  modules,
  activeSource,
  onActiveSourceChange,
  onModuleChange,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  open: boolean
  apiUrl: string
  credentialStored: boolean
  credentialSource: KualiResearchFeedOptions['credentialSource']
  modules: KualiResearchModuleOptions[]
  activeSource: KualiResearchSourceType
  onActiveSourceChange: (source: KualiResearchSourceType) => void
  onModuleChange: (module: KualiResearchModuleOptions) => void
}) {
  const activeModule = modules.find(module => module.source === activeSource) ?? modules[0]
  if (!activeModule) return null

  return (
    <div className="overflow-hidden rounded-[11px] border border-slate-200 bg-white">
      <div
        role="tablist"
        aria-label="Enabled Research modules"
        className="flex gap-0.5 overflow-x-auto border-b border-slate-200 bg-slate-50 px-1.5 pt-1.5"
      >
        {modules.map(module => {
          const definition = KUALI_RESEARCH_SOURCES.find(source => source.value === module.source)
          const active = module.source === activeModule.source
          return (
            <button
              key={module.source}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`kuali-${module.source}-panel`}
              id={`kuali-${module.source}-tab`}
              className={cn(
                'relative top-px shrink-0 rounded-t-lg border border-b-0 px-3 pb-2 pt-1.5 text-[12.5px] font-semibold',
                active
                  ? 'border-slate-200 bg-white text-slate-950'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              )}
              onClick={() => onActiveSourceChange(module.source)}
            >
              {definition?.label}
            </button>
          )
        })}
      </div>
      <div
        role="tabpanel"
        id={`kuali-${activeModule.source}-panel`}
        aria-labelledby={`kuali-${activeModule.source}-tab`}
        className="p-3.5"
      >
        <KualiResearchModuleScope
          api={api}
          orgId={orgId}
          vaultId={vaultId}
          open={open}
          apiUrl={apiUrl}
          credentialStored={credentialStored}
          credentialSource={credentialSource}
          module={activeModule}
          onChange={onModuleChange}
        />
      </div>
    </div>
  )
}
